import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { NetworkCanvas } from "../components/NetworkCanvas";
import { LOGO } from "../data/content";

const CONCEPTS = [
  {
    to: "/concept-1",
    n: "01",
    name: "Command Centre",
    desc: "Dark navy and gold. Confident, secure, premium — a strong enterprise feel that develops the coming-soon identity.",
    traits: ["Dark navy canvas", "Animated infrastructure mesh", "Enterprise restraint"],
    swatches: ["#0a1526", "#0d1b30", "#bd8931", "#d4af5e"],
    font: "font-marcellus",
    testid: "picker-concept-1",
  },
  {
    to: "/concept-2",
    n: "02",
    name: "Future Infrastructure",
    desc: "Clean white, charcoal and gold. Architectural imagery, index-style layouts and an innovation-led editorial feel.",
    traits: ["Light architectural", "Editorial typography", "Structured index grids"],
    swatches: ["#ffffff", "#f7f4ee", "#1a1a18", "#bd8931"],
    font: "font-archivo",
    testid: "picker-concept-2",
  },
  {
    to: "/concept-3",
    n: "03",
    name: "Intelligent Spaces",
    desc: "Rich visual imagery of secure buildings, connected workplaces and communities — a warmer, human, approachable feel.",
    traits: ["Full-bleed imagery", "Rounded, warm surfaces", "People-first messaging"],
    swatches: ["#f7f4ee", "#0a1526", "#bd8931", "#ffffff"],
    font: "font-outfit",
    testid: "picker-concept-3",
  },
];

export default function ConceptPicker() {
  return (
    <div className="relative min-h-screen bg-nx-navy font-publicsans text-slate-200 antialiased overflow-hidden" data-testid="concept-picker">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#10203a_0%,#0a1526_55%,#060d1a_100%)]" />
      <NetworkCanvas skyline className="absolute inset-0 w-full h-full opacity-60" />
      <div className="relative nx-wrap pt-14 sm:pt-20 pb-32">
        <div className="flex flex-col items-center text-center mb-16">
          <img src={LOGO.fullDark} alt="NexSecure Technologies" className="h-40 sm:h-52 w-auto mb-8" data-testid="picker-logo" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight mb-4">Homepage Design Concepts</span>
          <h1 className="font-marcellus text-3xl sm:text-4xl lg:text-5xl text-white max-w-2xl leading-tight" data-testid="picker-title">
            Three directions for Engineering Intelligent Infrastructure
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-400 max-w-xl leading-relaxed">
            Review each concept in full — hero animation, solutions, Kerala coverage and the enquiry experience — then tell us which one to build out into the complete website.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {CONCEPTS.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={c.testid}
              className="group relative border border-white/10 bg-white/[0.03] backdrop-blur-md p-8 flex flex-col transition-all duration-300 hover:border-nx-gold/50 hover:bg-white/[0.05] hover:-translate-y-1"
            >
              <span className="font-marcellus text-nx-gold/60 text-xl">{c.n}</span>
              <h2 className={`${c.font} text-2xl text-white mt-4`}>{c.name}</h2>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed flex-1">{c.desc}</p>
              <ul className="mt-5 flex flex-col gap-2">
                {c.traits.map((t) => (
                  <li key={t} className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="w-1 h-1 rounded-full bg-nx-goldlight" /> {t}
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex gap-2">
                {c.swatches.map((s) => (
                  <span key={s} className="w-6 h-6 rounded-full border border-white/20" style={{ background: s }} />
                ))}
              </div>
              <span className="mt-7 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-nx-goldlight">
                View Concept <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
        <p className="text-center text-xs text-slate-500 mt-12" data-testid="picker-note">
          All three concepts use the approved content, official logo, and the same enquiry flow. Your selection becomes the foundation for the full NexSecure website.
        </p>
      </div>
    </div>
  );
}
